// =============================================================================
// Panel właściwości zaznaczonego węzła + edytor wyrażeń.
//
// WYRAŻENIE JEST LISTĄ TOKENÓW, nie stringiem z wąsami. Gramatyka z E0 zna
// trzy tokeny — `{lit}` (tekst dosłowny), `{var}` (zmienna wzoru) i `{pole}`
// (kolumna wiersza wewnątrz powtórzenia) — i skleja je BEZ separatora i BEZ
// logiki. To nie jest ubogi szablon; to jest decyzja: brak `if` i brak pętli
// w wyrażeniu znaczy, że układ zawsze da się odtworzyć i sprawdzić.
//
// Dlatego edytor pokazuje tokeny jako osobne wiersze, a zmienną wybiera się
// z LISTY. Pole tekstowe z `{{ }}` byłoby wygodniejsze do wpisania i o klasę
// gorsze do sprawdzenia: literówka w nazwie zmiennej wychodziłaby dopiero
// przy publikacji, a nie w chwili wyboru.
//
// ZAWĘŻANIE, NIE RZUTOWANIE. `LayoutNode` jest unią rozłączną po `kind`.
// Wewnątrz `case` węzeł jest już zawężony, więc `Wezel<'text'>` pozwala
// rozszerzyć go o pole tego rodzaju bez rzutowania przez `unknown` — a to
// właśnie takie rzutowanie przepuściłoby `bold` na `repeat`.
// =============================================================================

import type { LayoutNode, Token, Wyrazenie } from '@insolitum/print-templates';
import {
  KSZTALTY_TERAZ,
  NAZWA_KSZTALTU_TERAZ,
  type KsztaltTeraz,
} from '@insolitum/print-templates/uklad';
import { Badge, Button, Input, Select, Switch } from '@insolitum/ui';
import { Plus, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import type { ZasobDruku } from '../printCoreApi';

import { DOMYSLNY_ROZMIAR_PT } from './stan';

/** Węzeł konkretnego rodzaju — do bezpiecznego rozszerzania w `case`. */
type Wezel<K extends LayoutNode['kind']> = Extract<LayoutNode, { kind: K }>;

/** Zmienna wzoru w postaci, jakiej potrzebuje lista wyboru. */
export interface ZmiennaWzoru {
  name: string;
  description?: string;
  optional?: boolean;
}

/**
 * Pole z etykietą. `Input` z biblioteki UI etykiety NIE MA (przyjmuje tylko
 * `error`/`errorMessage`), a formularz bez etykiet jest nie do przejścia
 * klawiaturą i nie do odczytania czytnikiem ekranu.
 */
function Pole({
  etykieta,
  wartosc,
  onZmien,
  placeholder,
  podpowiedz,
}: {
  etykieta: string;
  wartosc: string;
  onZmien: (v: string) => void;
  placeholder?: string;
  podpowiedz?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-aurora-muted">{etykieta}</span>
      <Input value={wartosc} placeholder={placeholder} onChange={(e) => onZmien(e.target.value)} />
      {podpowiedz && (
        <span className="mt-1 block text-[11px] text-aurora-subtle">{podpowiedz}</span>
      )}
    </label>
  );
}

/**
 * Liczba z pola tekstowego — pusty albo niepoprawny wpis NIE znaczy zera.
 *
 * Pusty ciąg trzeba odciąć OSOBNO: `Number('')` to `0`, a `0` jest skończone,
 * więc sam `Number.isFinite` przepuszczał je jako poprawną wartość. Skasowanie
 * zawartości pola „Rozmiar (pt)" ustawiało `sizePt: 0` — napis znikał
 * z płótna, a publikacja wracała 422-ką o polu, którego nikt świadomie nie
 * ruszał.
 */
function liczba(wartosc: string, domyslna: number): number {
  const t = wartosc.trim();
  if (t === '') return domyslna;
  const v = Number(t.replace(',', '.'));
  return Number.isFinite(v) ? Math.round(v * 100) / 100 : domyslna;
}

interface Props {
  node: LayoutNode | null;
  zmienne: ZmiennaWzoru[];
  /** Cele wzoru — decydują, czy wybór kroju w ogóle ma prawo się pokazać. */
  cele: string[];
  /** Obrazy wgrane przez tenanta (E6) — do wyboru w węźle `image`. */
  zasoby: ZasobDruku[];
  /** Wgranie nowego pliku; `null` = nie udało się (komunikat pokazuje edytor). */
  onWgrajZasob: (plik: File) => Promise<ZasobDruku | null>;
  onZmien: (fn: (n: LayoutNode) => LayoutNode) => void;
  onUsun: () => void;
}

export function PanelWlasciwosci({
  node,
  zmienne,
  cele,
  zasoby,
  onWgrajZasob,
  onZmien,
  onUsun,
}: Props) {
  if (!node) {
    return (
      <p className="p-4 text-xs text-aurora-subtle">
        Zaznacz element na płótnie, żeby zobaczyć jego właściwości.
      </p>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <Badge>{node.kind}</Badge>
        <Button variant="ghost" size="sm" onClick={onUsun} disabled={node.zablokowany}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <Pole
        etykieta="Nazwa w warstwach"
        wartosc={node.nazwa ?? ''}
        placeholder={node.kind}
        onZmien={(v) => onZmien((n) => ({ ...n, nazwa: v || undefined }))}
      />

      <div className="flex gap-4">
        <Switch
          label="Ukryty"
          checked={node.ukryty === true}
          onChange={(e) => onZmien((n) => ({ ...n, ukryty: e.target.checked || undefined }))}
        />
        <Switch
          label="Zablokowany"
          checked={node.zablokowany === true}
          onChange={(e) => onZmien((n) => ({ ...n, zablokowany: e.target.checked || undefined }))}
        />
      </div>

      <PolaPozycji node={node} onZmien={onZmien} />
      <PolaRodzaju
        node={node}
        zmienne={zmienne}
        cele={cele}
        zasoby={zasoby}
        onWgrajZasob={onWgrajZasob}
        onZmien={onZmien}
      />
    </div>
  );
}

function PolaPozycji({
  node,
  onZmien,
}: {
  node: LayoutNode;
  onZmien: (fn: (n: LayoutNode) => LayoutNode) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {/* `repeat` nie ma `xMm` — wiersze rozkłada `stepMm` na całej szerokości. */}
      {node.kind !== 'repeat' && (
        <Pole
          etykieta="X (mm)"
          wartosc={String(node.xMm)}
          onZmien={(v) =>
            onZmien((n) => (n.kind === 'repeat' ? n : { ...n, xMm: liczba(v, n.xMm) }))
          }
        />
      )}
      <Pole
        etykieta="Y (mm)"
        wartosc={String(node.yMm)}
        onZmien={(v) => onZmien((n) => ({ ...n, yMm: liczba(v, n.yMm) }))}
      />
    </div>
  );
}

function PolaRodzaju({
  node,
  zmienne,
  cele,
  zasoby,
  onWgrajZasob,
  onZmien,
}: {
  node: LayoutNode;
  zmienne: ZmiennaWzoru[];
  cele: string[];
  zasoby: ZasobDruku[];
  onWgrajZasob: (plik: File) => Promise<ZasobDruku | null>;
  onZmien: (fn: (n: LayoutNode) => LayoutNode) => void;
}) {
  switch (node.kind) {
    case 'text':
      return (
        <>
          <EdytorWyrazenia
            etykieta="Treść"
            wyrazenie={node.tekst}
            zmienne={zmienne}
            onZmien={(w) => onZmien((n) => ({ ...(n as Wezel<'text'>), tekst: w }))}
          />
          <div className="grid grid-cols-2 gap-2">
            <Pole
              etykieta="Rozmiar (pt)"
              wartosc={String(node.sizePt ?? DOMYSLNY_ROZMIAR_PT)}
              onZmien={(v) =>
                onZmien((n) => ({
                  ...(n as Wezel<'text'>),
                  sizePt: liczba(v, DOMYSLNY_ROZMIAR_PT),
                }))
              }
            />
            <Select
              label="Wyrównanie"
              value={node.align ?? 'left'}
              onChange={(e) =>
                onZmien((n) => ({
                  ...(n as Wezel<'text'>),
                  align: e.target.value as 'left' | 'center' | 'right',
                }))
              }
              options={[
                { value: 'left', label: 'Do lewej' },
                { value: 'center', label: 'Do środka' },
                { value: 'right', label: 'Do prawej' },
              ]}
              helperText={
                node.maxWidthMm === undefined
                  ? 'Zadziała po nadaniu szerokości bloku — bez niej napis zajmuje dokładnie tyle, ile ma znaków, i nie ma czego wyrównywać.'
                  : undefined
              }
            />
          </div>
          <PoleSzerokosciBloku node={node} onZmien={onZmien} />
          <Switch
            label="Pogrubienie"
            checked={node.bold === true}
            onChange={(e) =>
              onZmien((n) => ({
                ...(n as Wezel<'text'>),
                bold: e.target.checked || undefined,
              }))
            }
          />
          <PoleKroju node={node} cele={cele} onZmien={onZmien} />
        </>
      );

    case 'barcode':
      return (
        <>
          <EdytorWyrazenia
            etykieta="Dane kodu"
            wyrazenie={node.dane}
            zmienne={zmienne}
            onZmien={(w) => onZmien((n) => ({ ...(n as Wezel<'barcode'>), dane: w }))}
          />
          <Select
            label="Symbolika"
            value={node.symbology}
            onChange={(e) =>
              onZmien((n) => ({
                ...(n as Wezel<'barcode'>),
                symbology: e.target.value as 'code128' | 'ean13',
              }))
            }
            options={[
              { value: 'code128', label: 'Code 128' },
              { value: 'ean13', label: 'EAN-13 (13 cyfr)' },
            ]}
            helperText="EAN-13 przyjmuje wyłącznie cyfry — enkoder odrzuci resztę przy publikacji."
          />
          <Pole
            etykieta="Wysokość (mm)"
            wartosc={String(node.heightMm)}
            onZmien={(v) =>
              onZmien((n) => ({ ...(n as Wezel<'barcode'>), heightMm: liczba(v, 12) }))
            }
          />
          <Switch
            label="Podpis pod kodem"
            checked={node.showText !== false}
            onChange={(e) =>
              onZmien((n) => ({ ...(n as Wezel<'barcode'>), showText: e.target.checked }))
            }
          />
        </>
      );

    case 'qr':
      return (
        <>
          <EdytorWyrazenia
            etykieta="Dane QR"
            wyrazenie={node.dane}
            zmienne={zmienne}
            onZmien={(w) => onZmien((n) => ({ ...(n as Wezel<'qr'>), dane: w }))}
          />
          <Pole
            etykieta="Bok (mm)"
            wartosc={String(node.sizeMm)}
            onZmien={(v) => onZmien((n) => ({ ...(n as Wezel<'qr'>), sizeMm: liczba(v, 20) }))}
          />
        </>
      );

    case 'line':
      return (
        <div className="grid grid-cols-2 gap-2">
          <Pole
            etykieta="X końca (mm)"
            wartosc={String(node.x2Mm)}
            onZmien={(v) =>
              onZmien((n) => ({ ...(n as Wezel<'line'>), x2Mm: liczba(v, node.x2Mm) }))
            }
          />
          <Pole
            etykieta="Y końca (mm)"
            wartosc={String(node.y2Mm)}
            onZmien={(v) =>
              onZmien((n) => ({ ...(n as Wezel<'line'>), y2Mm: liczba(v, node.y2Mm) }))
            }
          />
          <Pole
            etykieta="Grubość (mm)"
            wartosc={String(node.thicknessMm ?? 0.3)}
            onZmien={(v) =>
              onZmien((n) => ({ ...(n as Wezel<'line'>), thicknessMm: liczba(v, 0.3) }))
            }
          />
        </div>
      );

    case 'box':
      return (
        <div className="grid grid-cols-2 gap-2">
          <Pole
            etykieta="Szerokość (mm)"
            wartosc={String(node.widthMm)}
            onZmien={(v) =>
              onZmien((n) => ({ ...(n as Wezel<'box'>), widthMm: liczba(v, node.widthMm) }))
            }
          />
          <Pole
            etykieta="Wysokość (mm)"
            wartosc={String(node.heightMm)}
            onZmien={(v) =>
              onZmien((n) => ({ ...(n as Wezel<'box'>), heightMm: liczba(v, node.heightMm) }))
            }
          />
          <Pole
            etykieta="Grubość (mm)"
            wartosc={String(node.thicknessMm ?? 0.3)}
            onZmien={(v) =>
              onZmien((n) => ({ ...(n as Wezel<'box'>), thicknessMm: liczba(v, 0.3) }))
            }
          />
        </div>
      );

    case 'image':
      return (
        <>
          <Select
            label="Źródło obrazu"
            value={node.zrodlo.rodzaj}
            onChange={(e) =>
              onZmien((n) => {
                const w = n as Wezel<'image'>;
                if (e.target.value === 'logo_organizacji') {
                  return { ...w, zrodlo: { rodzaj: 'logo_organizacji' } };
                }
                if (e.target.value === 'zmienna') {
                  return { ...w, zrodlo: { rodzaj: 'zmienna', var: '' } };
                }
                return { ...w, zrodlo: { rodzaj: 'zasob', assetId: '' } };
              })
            }
            options={[
              { value: 'zasob', label: 'Wgrany plik' },
              { value: 'zmienna', label: 'Zmienna wzoru (obraz)' },
              { value: 'logo_organizacji', label: 'Logo organizacji' },
            ]}
            helperText={
              node.zrodlo.rodzaj === 'logo_organizacji'
                ? 'Logo organizacji nie ma jeszcze skąd wziąć bajtów — publikacja takiego układu zostanie odrzucona. Wgraj plik i wskaż go jako zasób.'
                : undefined
            }
          />
          {node.zrodlo.rodzaj === 'zasob' && (
            <WyborZasobu
              wybrany={node.zrodlo.assetId}
              zasoby={zasoby}
              onWybierz={(asset) =>
                onZmien((n) => {
                  const w = n as Wezel<'image'>;
                  // Proporcja z ORYGINAŁU, przy zachowanej szerokości. Bez tego
                  // każdy wgrany plik siedziałby w kwadracie 20×20 mm i trzeba
                  // by go ręcznie przeliczać — czyli robić rzecz, po której
                  // widać, że nikt tego nie używał.
                  const wysokosc =
                    Math.round(((w.widthMm * asset.height_px) / asset.width_px) * 10) / 10;
                  return {
                    ...w,
                    zrodlo: { rodzaj: 'zasob', assetId: asset.id },
                    heightMm: Number.isFinite(wysokosc) && wysokosc > 0 ? wysokosc : w.heightMm,
                    alt: w.alt ?? asset.nazwa,
                  };
                })
              }
              onWgraj={onWgrajZasob}
            />
          )}
          {node.zrodlo.rodzaj === 'zmienna' && (
            <Select
              label="Zmienna"
              value={node.zrodlo.var}
              onChange={(e) =>
                onZmien((n) => ({
                  ...(n as Wezel<'image'>),
                  zrodlo: { rodzaj: 'zmienna', var: e.target.value },
                }))
              }
              options={[
                { value: '', label: '— wybierz —' },
                ...zmienne.map((z) => ({ value: z.name, label: z.name })),
              ]}
            />
          )}
          <div className="grid grid-cols-2 gap-2">
            <Pole
              etykieta="Szerokość (mm)"
              wartosc={String(node.widthMm)}
              onZmien={(v) =>
                onZmien((n) => ({ ...(n as Wezel<'image'>), widthMm: liczba(v, node.widthMm) }))
              }
            />
            <Pole
              etykieta="Wysokość (mm)"
              wartosc={String(node.heightMm)}
              onZmien={(v) =>
                onZmien((n) => ({ ...(n as Wezel<'image'>), heightMm: liczba(v, node.heightMm) }))
              }
            />
          </div>
        </>
      );

    case 'repeat':
      return (
        <>
          <Pole
            etykieta="Odstęp wierszy (mm)"
            wartosc={String(node.stepMm)}
            onZmien={(v) =>
              onZmien((n) => ({ ...(n as Wezel<'repeat'>), stepMm: liczba(v, node.stepMm) }))
            }
          />
          <p className="text-xs text-aurora-subtle">
            Zawartość powtórzenia edytuje się na płótnie — kliknij element wewnątrz ramki.
          </p>
        </>
      );
  }
}

/**
 * Wybór wgranego obrazu + wgrywanie nowego.
 *
 * `<input type="file">` schowany za przyciskiem, bo natywna kontrolka pliku
 * wygląda inaczej w każdej przeglądarce i nie da się jej ubrać w Aurorę.
 *
 * Lista pokazuje WYMIARY, a nie tylko nazwę: to one decydują, czy logo
 * 1200×300 wejdzie na etykietę 80 mm, a poza edytorem nie ma ich gdzie
 * sprawdzić.
 */
function WyborZasobu({
  wybrany,
  zasoby,
  onWybierz,
  onWgraj,
}: {
  wybrany: string;
  zasoby: ZasobDruku[];
  onWybierz: (z: ZasobDruku) => void;
  onWgraj: (plik: File) => Promise<ZasobDruku | null>;
}) {
  const wejscie = useRef<HTMLInputElement>(null);
  const [wgrywa, setWgrywa] = useState(false);

  const wybierzPlik = async (plik: File | undefined) => {
    if (!plik) return;
    setWgrywa(true);
    try {
      const asset = await onWgraj(plik);
      if (asset) onWybierz(asset);
    } finally {
      setWgrywa(false);
      // Bez czyszczenia ponowny wybór TEGO SAMEGO pliku nie odpala `change`
      // i przycisk wygląda na zepsuty.
      if (wejscie.current) wejscie.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <Select
        label="Plik"
        value={wybrany}
        onChange={(e) => {
          const z = zasoby.find((x) => x.id === e.target.value);
          if (z) onWybierz(z);
        }}
        options={[
          { value: '', label: zasoby.length === 0 ? '— brak wgranych plików —' : '— wybierz —' },
          ...zasoby.map((z) => ({
            value: z.id,
            label: `${z.nazwa} (${z.width_px}×${z.height_px})`,
          })),
        ]}
      />
      <input
        ref={wejscie}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => void wybierzPlik(e.target.files?.[0])}
      />
      <Button
        variant="secondary"
        size="sm"
        loading={wgrywa}
        onClick={() => wejscie.current?.click()}
      >
        <Upload className="h-4 w-4" />
        <span className="ml-1.5">Wgraj obraz</span>
      </Button>
      <p className="text-[11px] text-aurora-subtle">
        PNG, JPEG albo WebP, do 2 MB. Ten sam plik wgrany drugi raz nie mnoży pozycji — serwer
        rozpoznaje go po sumie kontrolnej.
      </p>
    </div>
  );
}

/**
 * Wybór kroju — WIDOCZNY WYŁĄCZNIE tam, gdzie da się go dotrzymać.
 *
 * Drukarki etykiet mają fonty urządzenia: ZPL zna jeden krój skalowalny
 * (`^A0`), a EPL i TSPL dobierają font bitmapowy z ROZMIARU. Wybór kroju na
 * takim wzorze byłby obietnicą, którą łamie dopiero naklejka — dlatego
 * walidator go tam odrzuca (`kroj_nieobslugiwany`), a panel nawet nie pokazuje
 * kontrolki. Pokazuje za to ZDANIE, dlaczego jej nie ma: kontrolka, która
 * zniknęła bez wyjaśnienia, czyta się jak usterka panelu.
 */
/**
 * SZEROKOŚĆ BLOKU — jedyne pole, które zamienia napis w akapit.
 *
 * `maxWidthMm` istnieje w gramatyce od E0 i honoruje je WSZYSTKO: facsimile
 * (`width` + `white-space: normal`), a ZPL, EPL i TSPL łamią po tej samej
 * liczbie znaków; wzory katalogowe używają go od zawsze (`maxWidthMm: 72`).
 * Brakowało wyłącznie pola w panelu — więc z edytora nie dało się zrobić
 * tekstu, który zawija, ani takiego, który da się wyrównać do środka, bo
 * `align` poza blokiem jest ignorowane przez każdy z czterech celów.
 *
 * PUSTE POLE KASUJE KLUCZ, a nie ustawia zera. `maxWidthMm: 0` byłoby blokiem
 * o zerowej szerokości: walidator odrzuca go jako liczbę niedodatnią, a do
 * tego czasu płótno rysuje z napisu kolumnę szeroką na jeden znak. „Bez
 * zawijania" to BRAK pola — tak samo zapisuje to gramatyka.
 */
function PoleSzerokosciBloku({
  node,
  onZmien,
}: {
  node: Wezel<'text'>;
  onZmien: (fn: (n: LayoutNode) => LayoutNode) => void;
}) {
  return (
    <Pole
      etykieta="Szerokość bloku (mm)"
      wartosc={node.maxWidthMm === undefined ? '' : String(node.maxWidthMm)}
      placeholder="bez zawijania"
      podpowiedz="Pusto — napis w jednej linii. Z szerokością tekst zawija się w blok i zaczyna działać wyrównanie."
      onZmien={(v) =>
        onZmien((n) => {
          const w = { ...(n as Wezel<'text'>) };
          if (v.trim() === '') {
            delete w.maxWidthMm;
            return w;
          }
          return { ...w, maxWidthMm: Math.max(0.1, liczba(v, 1)) };
        })
      }
    />
  );
}

function PoleKroju({
  node,
  cele,
  onZmien,
}: {
  node: Wezel<'text'>;
  cele: string[];
  onZmien: (fn: (n: LayoutNode) => LayoutNode) => void;
}) {
  const termiczne = cele.filter((c) => c !== 'PDF');
  if (termiczne.length > 0) {
    return (
      <p className="text-[11px] text-aurora-subtle">
        Krój pisma: ten wzór drukuje się na {termiczne.join(', ')}, a te drukarki mają własne fonty
        urządzenia — zostaje rozmiar, pogrubienie i wyrównanie.
      </p>
    );
  }
  return (
    <Select
      label="Krój pisma"
      value={node.kroj ?? 'ui'}
      onChange={(e) =>
        onZmien((n) => ({
          ...(n as Wezel<'text'>),
          kroj: e.target.value === 'ui' ? undefined : (e.target.value as 'serif' | 'mono'),
        }))
      }
      options={[
        { value: 'ui', label: 'Bezszeryfowy (domyślny)' },
        { value: 'serif', label: 'Szeryfowy' },
        { value: 'mono', label: 'O stałej szerokości' },
      ]}
    />
  );
}

// ── Edytor wyrażenia ─────────────────────────────────────────────────────────

type RodzajTokenu = 'lit' | 'var' | 'pole' | 'teraz';

function rodzajTokenu(t: Token): RodzajTokenu {
  if ('lit' in t) return 'lit';
  if ('var' in t) return 'var';
  if ('teraz' in t) return 'teraz';
  return 'pole';
}

function wartoscTokenu(t: Token): string {
  if ('lit' in t) return t.lit;
  if ('var' in t) return t.var;
  if ('teraz' in t) return t.teraz;
  return t.pole;
}

function zbudujToken(rodzaj: RodzajTokenu, wartosc: string): Token {
  if (rodzaj === 'lit') return { lit: wartosc };
  if (rodzaj === 'var') return { var: wartosc };
  if (rodzaj === 'teraz') {
    // Zamiana rodzaju NIE MOŻE przepisać wartości wprost: po przełączeniu
    // z „Tekst" wartością byłby dowolny napis, a `{teraz:'Partia'}` jest
    // układem, którego walidator nie przyjmie. Kształt spoza zbioru wraca
    // więc do domyślnej daty.
    const ksztalt = KSZTALTY_TERAZ.includes(wartosc as KsztaltTeraz)
      ? (wartosc as KsztaltTeraz)
      : 'data';
    return { teraz: ksztalt };
  }
  return { pole: wartosc };
}

export function EdytorWyrazenia({
  etykieta,
  wyrazenie,
  zmienne,
  onZmien,
}: {
  etykieta: string;
  wyrazenie: Wyrazenie;
  zmienne: ZmiennaWzoru[];
  onZmien: (w: Wyrazenie) => void;
}) {
  const podmien = (i: number, t: Token) => onZmien(wyrazenie.map((x, j) => (j === i ? t : x)));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-aurora-muted">{etykieta}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onZmien([...wyrazenie, { lit: '' }])}
          title="Dodaj fragment"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {wyrazenie.length === 0 && (
        <p className="text-xs text-aurora-subtle">
          Puste wyrażenie — element nie wydrukuje nic. Dodaj fragment.
        </p>
      )}

      {wyrazenie.map((token, i) => {
        const rodzaj = rodzajTokenu(token);
        return (
          <div key={i} className="flex items-end gap-2">
            <Select
              value={rodzaj}
              onChange={(e) =>
                podmien(i, zbudujToken(e.target.value as RodzajTokenu, wartoscTokenu(token)))
              }
              options={[
                { value: 'lit', label: 'Tekst' },
                { value: 'var', label: 'Zmienna' },
                { value: 'pole', label: 'Pole wiersza' },
                { value: 'teraz', label: 'Chwila wydruku' },
              ]}
            />
            {rodzaj === 'teraz' ? (
              <Select
                value={wartoscTokenu(token)}
                onChange={(e) => podmien(i, { teraz: e.target.value as KsztaltTeraz })}
                options={KSZTALTY_TERAZ.map((k) => ({
                  value: k,
                  label: NAZWA_KSZTALTU_TERAZ[k],
                }))}
              />
            ) : rodzaj === 'var' ? (
              <Select
                value={wartoscTokenu(token)}
                onChange={(e) => podmien(i, { var: e.target.value })}
                options={[
                  { value: '', label: '— wybierz —' },
                  ...zmienne.map((z) => ({
                    value: z.name,
                    label: z.optional ? `${z.name} (opcjonalna)` : z.name,
                  })),
                ]}
              />
            ) : (
              <Input
                value={wartoscTokenu(token)}
                onChange={(e) => podmien(i, zbudujToken(rodzaj, e.target.value))}
                placeholder={rodzaj === 'pole' ? 'nazwa kolumny' : 'tekst'}
              />
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onZmien(wyrazenie.filter((_, j) => j !== i))}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}
